// Sprint-16 — in-app notification store.
//
// One Zustand store owns:
//   • items           — the latest N rows the user has seen (cap 50 in cache).
//   • unreadCount     — drives the bell badge. Pulled separately so the badge
//                       can refresh without re-fetching the entire list.
//   • refresh()       — fetch unread count + the latest N rows.
//   • markRead(id)    — optimistic flip + POST.
//   • markAllRead()   — optimistic flip-all + POST.
//
// IMPORTANT — every consumer reads with INDIVIDUAL selectors only. Never
// write `useNotifications(s => ({ a, b }))`; returning a new object on
// every state change triggers Zustand's shallow-equality re-render loop.
// Project rule, same as watchStore + threadsStore.
//
// Polling cadence:
//   • On mount of InboxBell, call `attachNotificationPollers()`.
//   • Refresh runs immediately, on every window focus, and every 60s while
//     the tab is active (matches the unwatched-badge cadence in watchStore).

import { create } from 'zustand';
import { notificationsApi } from '../services/notificationsApi';
import type { Notification } from '../types/notification';

const MAX_ITEMS_CACHED = 50;

// FE-rel-24 — monotonic request token. Each refresh() captures the current
// value; a resolution only commits if it is still the latest, so a slow fetch
// can't clobber a fresher one.
let refreshSeq = 0;

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  /** Tracks the initial fetch — used by the dropdown to render a spinner
   *  on first open rather than a flash of "no notifications". */
  hydrated: boolean;

  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useNotifications = create<NotificationsState>((set, get) => ({
  items: [],
  unreadCount: 0,
  hydrated: false,

  refresh: async () => {
    const seq = ++refreshSeq;
    try {
      // Two parallel fetches — the count endpoint is much cheaper than
      // listing (partial-index COUNT vs. a 50-row scan), so we get the
      // badge updated even if the list is slow.
      const [count, items] = await Promise.all([
        notificationsApi.unreadCount(),
        notificationsApi.list({ limit: MAX_ITEMS_CACHED }),
      ]);
      // Ignore a stale resolution superseded by a newer refresh.
      if (seq !== refreshSeq) return;
      set({ unreadCount: count, items, hydrated: true });
    } catch (e) {
      // Don't bin the existing state on a transient failure — the bell
      // keeps showing the last-known count rather than disappearing.
      if (seq !== refreshSeq) return;
      console.warn('[notificationsStore.refresh] failed', e);
      set({ hydrated: true });
    }
  },

  markRead: async (id) => {
    const prev = { items: get().items, count: get().unreadCount };
    // Optimistic flip. We only decrement count if this row was actually
    // unread — flipping an already-read row must not double-decrement.
    const target = prev.items.find(n => n.id === id);
    const wasUnread = !!target && !target.read_at;
    set({
      items: prev.items.map(n => n.id === id
        ? { ...n, read_at: n.read_at || new Date().toISOString() }
        : n),
      unreadCount: wasUnread ? Math.max(0, prev.count - 1) : prev.count,
    });
    try {
      await notificationsApi.markRead(id);
    } catch (e) {
      console.error('[notificationsStore.markRead] failed', e);
      set(prev);
    }
  },

  markAllRead: async () => {
    const prev = { items: get().items, count: get().unreadCount };
    const stamp = new Date().toISOString();
    set({
      items: prev.items.map(n => n.read_at ? n : { ...n, read_at: stamp }),
      unreadCount: 0,
    });
    try {
      await notificationsApi.markAllRead();
    } catch (e) {
      console.error('[notificationsStore.markAllRead] failed', e);
      set(prev);
    }
  },
}));

/**
 * Bootstraps focus-refresh + adaptive polling. Call once (e.g. from AppShell
 * or InboxBell on mount). Returns a teardown to unwire on unmount so
 * dev hot-reload doesn't accumulate timers.
 *
 * Cadence:
 *   • Visible tab → 20s interval (responsive while the user is watching).
 *   • Hidden tab  → 60s interval (back off; nobody's looking).
 *   • window.focus event fires an immediate one-shot refresh on top of the
 *     interval so re-entering a stale tab feels instant.
 *
 * Only ONE interval is ever live — visibilitychange clears the previous
 * timer before scheduling the new one. (Stacking intervals is the most
 * common bug when implementing this pattern.)
 */
export function attachNotificationPollers(): () => void {
  const refresh = () => useNotifications.getState().refresh();
  const FAST_MS = 20_000;
  const SLOW_MS = 60_000;

  let intervalId: number | null = null;
  let currentMs: number | null = null;

  const schedule = (ms: number) => {
    if (currentMs === ms && intervalId !== null) return;
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    intervalId = window.setInterval(refresh, ms);
    currentMs = ms;
  };

  const pickInterval = () =>
    (typeof document !== 'undefined' && document.visibilityState === 'visible')
      ? FAST_MS
      : SLOW_MS;

  refresh();
  schedule(pickInterval());

  const onFocus = () => refresh();
  const onVisibilityChange = () => {
    schedule(pickInterval());
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      refresh();
    }
  };

  window.addEventListener('focus', onFocus);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    window.removeEventListener('focus', onFocus);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };
}
